import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { connect } from "react-redux";
import * as R from "ramda";
import {
  specsheetSelectSpanType,
  specsheetSVGClick,
  specsheetHandleForm,
} from "../specsheet/specsheet.redux";
import { specsheetParser } from "../specsheet/specsheetParser";
import { renderPitchWithPolarity } from "../specsheet/pitchPolarity";
import {
  getSpanVisibility,
  effectiveSelectedSpan,
} from "../specsheet/spanVisibility";
import { incrementFraction, findInsertSize } from "../../utils/utils";
import {
  idx_data_insert_sizes,
  idx_data_insert_types,
} from "../db/datafixtures/datafixtures";

const getInsertSizesByType = (gridType) =>
  R.pipe(R.filter(R.propEq("type_id", R.path(["id"])(gridType))));

const UnSelectableTextColor = "#00187E";

const UnSelectableText = styled.text`
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -o-user-select: none;
  user-select: none;
`;

const clickableRectStyle = {
  fill: "transparent",
};
const UnSelectableTextFont = "12";
const UnSelectableTextFontFamily = "Poppins";

const getSelectedSpanTitle = (selectedSpan, t) => {
  switch (selectedSpan) {
    case "full":
      return t("Middle finger Full");
    case "cut":
      return t("Middle finger Cut");
    case "outer2_cut":
      return t("Middle finger Outer to Cut");
    case "hole_to_hole":
      return t("Middle finger Hole to Hole");

    default:
      return "";
  }
};

const checkValueEmpty = (val) => R.isNil(val) || R.isEmpty(val);

// The span row, in the order it is drawn. Centre and gap match where the four
// circles used to sit hard-coded (145/170/195/220).
const SPAN_BUTTONS = [
  { span: "full", letter: "F" },
  { span: "cut", letter: "C" },
  { span: "outer2_cut", letter: "O" },
  { span: "hole_to_hole", letter: "H" },
];
const SPAN_ROW_CENTER_X = 182.5;
const SPAN_ROW_GAP = 25;

const getSpanButtonFillColor = (selectedSpan, spanButton, formValues) => {
  if (selectedSpan === spanButton) {
    return "#000000";
  }

  switch (spanButton) {
    case "full":
      return checkValueEmpty(formValues.f1_span_full) ||
        checkValueEmpty(formValues.f2_span_full)
        ? "#FFFFFF"
        : "#6C757E";
    case "cut":
      return checkValueEmpty(formValues.f1_span_cut) ||
        checkValueEmpty(formValues.f2_span_cut)
        ? "#FFFFFF"
        : "#6C757E";
    case "outer2_cut":
      return checkValueEmpty(formValues.f1_span_outer2_cut) ||
        checkValueEmpty(formValues.f2_span_outer2_cut)
        ? "#FFFFFF"
        : "#6C757E";
    case "hole_to_hole":
      return checkValueEmpty(formValues.f1_span_hole_to_hole) ||
        checkValueEmpty(formValues.f2_span_hole_to_hole)
        ? "#FFFFFF"
        : "#6C757E";
    default:
      return "#FFFFFF";
  }
};

const getSpanButtonTextFillColor = (selectedSpan, spanButton, formValues) => {
  if (selectedSpan === spanButton) {
    return "#FFFFFF";
  }

  switch (spanButton) {
    case "full":
      return checkValueEmpty(formValues.f1_span_full) ||
        checkValueEmpty(formValues.f2_span_full)
        ? "#6C757E"
        : "#FFFFFF";
    case "cut":
      return checkValueEmpty(formValues.f1_span_cut) ||
        checkValueEmpty(formValues.f2_span_cut)
        ? "#6C757E"
        : "#FFFFFF";
    case "outer2_cut":
      return checkValueEmpty(formValues.f1_span_outer2_cut) ||
        checkValueEmpty(formValues.f2_span_outer2_cut)
        ? "#6C757E"
        : "#FFFFFF";
    case "hole_to_hole":
      return checkValueEmpty(formValues.f1_span_hole_to_hole) ||
        checkValueEmpty(formValues.f2_span_hole_to_hole)
        ? "#6C757E"
        : "#FFFFFF";

    default:
      return "#FFFFFF";
  }
};

const SpecsheetSVGComponent = ({
  isDesktop,
  isAndroid,
  appSettings,
  formValues,
  selectedSpan,
  specsheetSelectSpanType,
  specsheetSVGClick,
  specsheetHandleForm,
  isOval,
  isOvalPreviewOpen,
  isRightHander,
  openDropdown,
  rightPlusMinus,
  leftPlusMinus,
  forwardPlusMinus,
  backPlusMinus,
  forwardPlusMinusFingers,
  backPlusMinusFingers,
  isReadOnly,
  is2Hander,
  flaggedFields,
  onFieldClick,
  onFieldValueChange,
  activeField,
}) => {
  const { t } = useTranslation();

  const handleVacuChange = (vacu) => {
    // A caller that wants the taps for itself gets them, read-only or not --
    // the same escape hatch clickableRectOnClickHandler has right below.
    // Without it this toggle was simply dead on the scan review, which draws
    // the sheet read-only: the tap fell straight through the guard below.
    //
    // No OD adjustment on that path. Off a scanned sheet the OD values are
    // whatever the paper says, and recomputing them from a correction to the
    // vacu setting would overwrite what was read.
    if (onFieldValueChange) {
      onFieldValueChange("vacu", vacu);
      return;
    }

    // Guarded like clickableRectOnClickHandler, which it sits next to: this
    // sheet is also drawn read-only, by the audit modal and the scan review,
    // and a stray tap there would write straight into the live form.
    if (isReadOnly) return;

    let f1_od = formValues?.f1_od;
    let f2_od = formValues?.f2_od;
    const increment = appSettings.systemDefault.default_vacu_increment;

    if (vacu === "vacu") {
      if (formValues?.f1_od && formValues?.f1_od !== "") {
        f1_od = incrementFraction(f1_od, increment);
      }
      if (formValues?.f2_od && formValues?.f2_od !== "") {
        f2_od = incrementFraction(f2_od, increment);
      }
    } else if (appSettings.autoSuggestions.spec_sheet_auto_insert_od) {
      if (
        formValues?.f1_grip_type &&
        formValues?.f1_size &&
        R.trim(formValues?.f1_size) !== ""
      ) {
        const f1_grip_type = R.find(
          R.propEq("type", R.pathOr("", ["f1_grip_type"])(formValues))
        )(idx_data_insert_types);
        const insertSizesF1 = getInsertSizesByType(f1_grip_type)(
          idx_data_insert_sizes
        );
        f1_od = findInsertSize(formValues?.f1_size, insertSizesF1);
      }
      if (
        formValues?.f2_grip_type &&
        formValues?.f2_size &&
        R.trim(formValues?.f2_size) !== ""
      ) {
        const f2_grip_type = R.find(
          R.propEq("type", R.pathOr("", ["f2_grip_type"])(formValues))
        )(idx_data_insert_types);
        const insertSizesF2 = getInsertSizesByType(f2_grip_type)(
          idx_data_insert_sizes
        );
        f2_od = findInsertSize(formValues?.f2_size, insertSizesF2);
      }
    }
    specsheetHandleForm({
      specsheetValues: { vacu, f1_od, f2_od },
    });
  };

  const svgStyle = {
    marginTop: isOvalPreviewOpen ? "-37.0em" : "0px",
    MozTransition: "margin-top 0.3s ease",
    WebkitTransition: "margin-top 0.3s ease",
    OTransition: "margin-top 0.3s ease",
    transition: "margin-top 0.3s ease",
    fontSize: "10px",
    width: "100%",
  };

  // A scan review passes the fields the reader was unsure of; they are drawn
  // on the sheet itself rather than only listed, because "check the middle
  // finger span" means nothing until you see which box it is.
  const flagged = Array.isArray(flaggedFields) ? flaggedFields : [];
  const rectStyle = (field) => {
    if (activeField && field === activeField) {
      return {
        fill: "rgba(0, 24, 126, 0.14)",
        stroke: "#00187e",
        strokeWidth: 2,
        cursor: "pointer",
      };
    }
    if (flagged.indexOf(field) >= 0) {
      return {
        fill: "rgba(255, 193, 7, 0.28)",
        stroke: "#c79100",
        strokeWidth: 1,
        cursor: onFieldClick ? "pointer" : undefined,
      };
    }
    return onFieldClick
      ? { ...clickableRectStyle, cursor: "pointer" }
      : clickableRectStyle;
  };

  const clickableRectOnClickHandler = (field) => {
    // A caller that wants the taps for itself gets them, read-only or not.
    // The dispatch below drives whichever ModalSpecsheets is listening to the
    // store, which on the bowler screen is the one editing the live sheet.
    // The scan review is editing a proposal, not that sheet, so it takes the
    // field name and opens its own copy of the same picker against its own
    // values -- otherwise a tap in the review would edit the bowler's sheet.
    if (onFieldClick) {
      onFieldClick(field);
      return;
    }
    if (isReadOnly) return;
    let openDropdown = field;
    if (!isRightHander) {
      if (field.indexOf("f1_") >= 0) {
        openDropdown = field.replace("f1_", "f2_");
      } else if (field.indexOf("f2_") >= 0) {
        openDropdown = field.replace("f2_", "f1_");
      }
    }
    specsheetSVGClick({ openDropdown });
  };

  const strokeStyle = {
    stroke: "#212529",
    fill: "#212529",
    strokeWidthLine: "1",
    strokeWidthPath: "1.25",
  };

  // aqui condicion

  let f1_insertless = false;
  // Removed because users are not using this as expected
  if (
    (formValues?.f1_grip_type || "").toLowerCase() === "no inserts" ||
    formValues?.f1_grip_type === ""
  ) {
    f1_insertless = true;
  }

  let f2_insertless = false;
  // Removed because users are not using this as expected
  if (
    (formValues?.f2_grip_type || "").toLowerCase() === "no inserts" ||
    formValues?.f2_grip_type === ""
  ) {
    f2_insertless = true;
  }

  // Which of F / C / O / H this grip can actually produce, and the span to
  // draw once the selected one may have just been hidden.
  const spanVisibility = getSpanVisibility(formValues);
  const shownSpan = effectiveSelectedSpan(selectedSpan, spanVisibility);

  return formValues ? (
    <svg
      className="specsheet"
      version="1.1"
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      width={"100%"}
      height={
        isDesktop
          ? `calc(100% - 3em)`
          : isAndroid
          ? `calc(100vh - 30px)`
          : `calc(100vh - 3em - 50px)`
      }
      // In viewBox value 4to is the height of the svg image
      viewBox={is2Hander ? "15 110 325 165" : "15 110 325 350"}
      enableBackground="new 25 110 310 350"
      preserveAspectRatio={is2Hander ? "xMidYMin meet" : "xMidYMid meet"}
      style={svgStyle}
    >
      {/* Line Arrow Large, size left  */}
      {!is2Hander && (
        <line
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthLine}
          x1="156.5"
          y1="353"
          x2="121.7"
          y2="221.5"
        />
      )}
      {/* Arrow pointing down Thumb, size left  */}
      {!is2Hander && (
        <path
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          d="
                M158.5,360.5l1.225-13.541c0,0-3.199,6.082-8.989,2.381L158.5,360.5"
        />
      )}
      {/*Arrow pointing up Oval, size left */}
      {!is2Hander && (
        <path
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          d="
                M119.7,
                214l-1.226,
                13.541c0,
                0,
                3.2-6.083,
                8.99-2.38L119.7,
                214"
        />
      )}
      {/*Line Arrow Large, size right  */}
      {!is2Hander && (
        <line
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthLine}
          x1="205.5"
          y1="353"
          x2="240.3"
          y2="221.5"
        />
      )}
      {/* Arrow, pointing down Thumb, size right  */}
      {!is2Hander && (
        <path
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          d="
                M203.5,360.5l-1.226-13.541c0,0,3.199,6.082,8.989,2.381L203.5,360.5"
        />
      )}
      {/* Arrow, pointing up Oval, size right  */}
      {!is2Hander && (
        <path
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          d="
                M242.3,214l1.225,13.541c0,0-3.199-6.083-8.988-2.38L242.3,214"
        />
      )}
      <g>
        <line
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          x1="39.695"
          y1="131.662"
          x2="39.695"
          y2="128.441"
        />

        <path
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          d="
                    M39.695,123.416l-2.349,6.452c0,0,2.349-1.427,4.696,0L39.695,123.416"
        />
      </g>
      <g>
        <line
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          x1="321.103"
          y1="131.536"
          x2="321.103"
          y2="128.316"
        />

        <path
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          d="
                    M321.103,123.291l2.348,6.451c0,0-2.348-1.427-4.696,0L321.103,123.291"
        />
      </g>
      <g>
        <line
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          x1="39.703"
          y1="239.009"
          x2="39.703"
          y2="235.789"
        />

        <path
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          d="
                    M39.703,244.034l2.348-6.451c0,0-2.348,1.426-4.696,0L39.703,244.034"
        />
      </g>
      <g>
        <line
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          x1="321.103"
          y1="238.858"
          x2="321.103"
          y2="235.638"
        />

        <path
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          d="
                    M321.103,243.883l-2.349-6.451c0,0,2.349,1.427,4.696,0L321.103,243.883"
        />
      </g>
      <g>
        <line
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          x1="36.169"
          y1="183.642"
          x2="39.389"
          y2="183.642"
        />

        <path
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          d="
                    M31.144,183.642l6.451,2.348c0,0-1.426-2.348,0-4.695L31.144,183.642"
        />
      </g>
      <g>
        <line
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          x1="324.642"
          y1="183.642"
          x2="321.421"
          y2="183.642"
        />

        <path
          fill={strokeStyle.fill}
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeMiterlimit="10"
          d="
                    M329.667,183.642l-6.451,2.348c0,0,1.426-2.348,0-4.695L329.667,183.642"
        />
      </g>
      <g>
        {/*Line Arrow ForWard Thumb */}
        {!is2Hander && (
          <line
            fill={strokeStyle.fill}
            stroke={strokeStyle.stroke}
            x1="180.706"
            y1="314.062"
            x2="180.706"
            y2="310.842"
          />
        )}
        {/*Arrow ForWard Thumb */}
        {!is2Hander && (
          <path
            fill={strokeStyle.fill}
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="10"
            d="
                    M180.706,305.816l-2.348,6.451c0,0,2.348-1.427,4.695,0L180.706,305.816"
          />
        )}
      </g>
      <g>
        {/* Line Arrow Reverse Thumb */}
        {!is2Hander && (
          <line
            fill={strokeStyle.fill}
            stroke={strokeStyle.stroke}
            x1="180.706"
            y1="451.603"
            x2="180.706"
            y2="448.382"
          />
        )}
        <g>
          {/* Arrow Reverse Thumb */}
          {!is2Hander && (
            <path
              fill={strokeStyle.fill}
              stroke={strokeStyle.stroke}
              strokeWidth={strokeStyle.strokeWidthPath}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="10"
              d="
                        M180.706,456.628l2.348-6.451c0,0-2.348,1.426-4.695,0L180.706,456.628"
            />
          )}
        </g>
      </g>
      <g>
        {/* Line Arrow Left Thumb */}
        {!is2Hander && (
          <line
            fill={strokeStyle.fill}
            stroke={strokeStyle.stroke}
            x1="93.226"
            y1="380.763"
            x2="96.446"
            y2="380.763"
          />
        )}
        <g>
          {/* Arrow Left Thumb */}
          {!is2Hander && (
            <path
              fill={strokeStyle.fill}
              stroke={strokeStyle.stroke}
              strokeWidth={strokeStyle.strokeWidthPath}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeMiterlimit="10"
              d="
                        M88.201,380.763l6.45,2.348c0,0-1.426-2.348,0-4.695L88.201,380.763"
            />
          )}
        </g>
      </g>
      <g>
        {/* Line Arrow Right Thumb */}
        {!is2Hander && (
          <line
            fill={strokeStyle.fill}
            stroke={strokeStyle.stroke}
            x1="268.052"
            y1="380.7"
            x2="264.831"
            y2="380.7"
          />
        )}
        {/* Arrow Right Thumb */}
        {!is2Hander && (
          <path
            fill={strokeStyle.fill}
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeMiterlimit="10"
            d="
                    M273.077,380.7l-6.451,2.348c0,0,1.426-2.348,0-4.695L273.077,380.7"
          />
        )}
      </g>
      <g>
        <circle
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          cx="122.189"
          cy="183.648"
          r="30.454"
        />
        <circle
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          cx="239.446"
          cy="183.648"
          r="30.454"
        />
        {/* Circle Thumb */}
        {!is2Hander && (
          <circle
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            cx="180.706"
            cy="381.688"
            r="30.455"
          />
        )}
        {/* Reatangle f1SPAN, size left. Part 1 of 2 */}
        {!is2Hander && (
          <rect
            x="92.886"
            y="248.742"
            fill="#FFFFFF"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            strokeMiterlimit="10"
            width="78.08"
            height="44.16"
          />
        )}
        {/* Rectangle f1SPAN , size left. Part 2 of 2  */}
        {!is2Hander && (
          <rect
            x="92.886"
            y="248.742"
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            width="78.08"
            height="44.16"
          />
        )}
        {/* Rectangle f2SPAN, size right. Part 1 of 2 */}
        {!is2Hander && (
          <rect
            x="190.878"
            y="248.742"
            fill="#FFFFFF"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            strokeMiterlimit="10"
            width="78.08"
            height="44.16"
          />
        )}
        {/* /* Rectangle f2SPAN, size right. Part 2 of 2*/}
        {!is2Hander && (
          <rect
            x="190.878"
            y="248.742"
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            width="78.08"
            height="44.16"
          />
        )}
        {/* Bridge */}
        <rect
          x="158.167"
          y="171.103"
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          width="45.079"
          height="25.08"
        />
        {/* Rectangle Forward Thumb */}
        {!is2Hander && (
          <rect
            x="158.167"
            y="314.062"
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            width="45.079"
            height="28.521"
          />
        )}
        {/* REVERSE Thumb Rectangle */}
        {!is2Hander && (
          <rect
            x="158.134"
            y="419.862"
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            width="45.079"
            height="28.52"
          />
        )}
        {/* Rectangle Right Thumb */}
        {!is2Hander && (
          <rect
            x="219.727"
            y="366.502"
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            width="45.079"
            height="28.521"
          />
        )}
        {/* Rectangle Left Thumb */}
        {!is2Hander && (
          <rect
            x="96.446"
            y="366.502"
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            width="45.08"
            height="28.521"
          />
        )}
        <rect
          x="39.406"
          y="169.382"
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          width="45.08"
          height="28.521"
        />
        <rect
          x="276.323"
          y="169.382"
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          width="45.08"
          height="28.521"
        />
        <rect
          x="39.406"
          y="207.103"
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          width="45.08"
          height="28.52"
        />
        <rect
          x="276.323"
          y="207.103"
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          width="45.08"
          height="28.52"
        />
        <rect
          x="39.406"
          y="131.662"
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          width="45.08"
          height="28.521"
        />
        <rect
          x="276.323"
          y="131.662"
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          width="45.08"
          height="28.521"
        />
        <line
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          x1="91.601"
          y1="183.648"
          x2="152.779"
          y2="183.648"
        />
        <line
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          x1="208.856"
          y1="183.648"
          x2="270.035"
          y2="183.648"
        />
        {/* Line Diameter Thumb */}
        {!is2Hander && (
          <line
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            x1="150.117"
            y1="381.688"
            x2="211.296"
            y2="381.688"
          />
        )}
        {/* Line Insert Type Left */}
        <line
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeDasharray="2,2,2,2,2,2"
          x1="91.34"
          y1="143.616"
          x2="152.519"
          y2="143.616"
        />
        {/* Line Insert Type Right */}
        <line
          fill="none"
          stroke={strokeStyle.stroke}
          strokeWidth={strokeStyle.strokeWidthPath}
          strokeDasharray="2,2,2,2,2,2"
          x1="208.969"
          y1="143.616"
          x2="270.147"
          y2="143.616"
        />
        {/* Line Thumb Insert Type */}
        {!is2Hander && (
          <line
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            strokeDasharray="2,2,2,2,2,2"
            x1="230.095"
            y1="344.44"
            x2="308.06"
            y2="344.44"
          />
        )}
        {/* Line Flexibility */}
        {!is2Hander && (
          <line
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            strokeDasharray="2,2,2,2,2,2"
            x1="89.257"
            y1="344.44"
            x2="131.254"
            y2="344.44"
          />
        )}
        {/* Line Offset */}
        {!is2Hander && (
          <line
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            strokeDasharray="2,2,2,2,2,2"
            x1="89.257"
            y1="444.44"
            x2="131.254"
            y2="444.44"
          />
        )}
        {/* Line CLT */}
        {!is2Hander && (
          <line
            fill="none"
            stroke={strokeStyle.stroke}
            strokeWidth={strokeStyle.strokeWidthPath}
            strokeDasharray="2,2,2,2,2,2"
            x1="234.257"
            y1="444.44"
            x2="272.2547"
            y2="444.44"
          />
        )}
      </g>
      // TEXT AND INTERACTIONS
      <g>
        {!f1_insertless && !f2_insertless && (
          <g>
            {/* STD/VACU toggle — sits above the Bridge square.
                Outer corners rounded, inner edges square (each half is a path). */}
            <path
              d="M 180,146 L 154,146 A 4,4 0 0 0 150,150 L 150,162 A 4,4 0 0 0 154,166 L 180,166 Z"
              fill={formValues?.vacu === "std" ? "#00297A" : "#E0E1E2"}
            />
            <path
              d="M 180,146 L 206,146 A 4,4 0 0 1 210,150 L 210,162 A 4,4 0 0 1 206,166 L 180,166 Z"
              fill={formValues?.vacu === "vacu" ? "#00297A" : "#E0E1E2"}
            />
            <UnSelectableText
              transform="matrix(1 0 0 1 165 160)"
              fill={formValues?.vacu === "std" ? "#FFFFFF" : "#00000099"}
              textAnchor="middle"
              fontWeight="700"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="10"
            >
              STD
            </UnSelectableText>
            <UnSelectableText
              transform="matrix(1 0 0 1 195 160)"
              fill={formValues?.vacu === "vacu" ? "#FFFFFF" : "#00000099"}
              textAnchor="middle"
              fontWeight="700"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="10"
            >
              VACU
            </UnSelectableText>
            <rect
              x={150}
              y={146}
              width={30}
              height={20}
              style={clickableRectStyle}
              onClick={() => handleVacuChange("std")}
            />
            <rect
              x={180}
              y={146}
              width={30}
              height={20}
              style={clickableRectStyle}
              onClick={() => handleVacuChange("vacu")}
            />
          </g>
        )}
        <g>
          {/* CLT text titulo del clt */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 254 431.143)"
              fill={strokeStyle.fill}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="8.5"
            >
              {t("CLT")}
            </UnSelectableText>
          )}
        </g>
        <g>
          {/* CLT Value */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 255 442)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="14"
            >
              {formValues.clt}
            </UnSelectableText>
          )}
          {/* CLT Clickable rect transparent */}
          {!is2Hander && (
            <rect
              x={228.167}
              y={420.103}
              width={45.079}
              height={35.08}
              style={{ ...rectStyle("clt"), fill: "transparent" }}
              onClick={() => {
                clickableRectOnClickHandler("clt");
              }}
            />
          )}
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 181 204.1602)"
            fill={strokeStyle.fill}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {t("Bridge")}
          </UnSelectableText>
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 180 189)"
            fill={UnSelectableTextColor}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="14"
          >
            {formValues.bridge}
          </UnSelectableText>
          <rect
            x={158.167}
            y={161.103}
            width={45.079}
            height={45.08}
            style={rectStyle("bridge")}
            onClick={() => {
              clickableRectOnClickHandler("bridge");
            }}
          />
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 122 150.6001)"
            fill={strokeStyle.fill}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {t("Inserts")}
          </UnSelectableText>
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 122 140)"
            fill={UnSelectableTextColor}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize={formValues.f2_grip_type?.length > 16 ? "5.5" : "7"}
          >
            {formValues.f1_grip_type}
          </UnSelectableText>
          <rect
            x={90}
            y={125}
            width={70}
            height={25}
            style={rectStyle("f1_grip_type")}
            onClick={() => {
              clickableRectOnClickHandler("f1_grip_type");
            }}
          />
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 44.3164 242.4829)"
            fill={strokeStyle.fill}
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {t("Forward")}
          </UnSelectableText>
          <g>
            <UnSelectableText
              transform="matrix(1 0 0 1 62 225)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize={UnSelectableTextFont}
            >
              {formValues.f1_for &&
              formValues.f1_for !== "" &&
              formValues.f1_for !== "0.000" &&
              formValues.f1_for !== "0"
                ? forwardPlusMinusFingers
                : ""}
              {formValues.f1_for}
            </UnSelectableText>
            <rect
              x={39}
              y={206}
              width={46}
              height={30}
              style={rectStyle("f1_for")}
              onClick={() => {
                clickableRectOnClickHandler("f1_for");
              }}
            />
          </g>
        </g>
        <UnSelectableText
          transform="matrix(1 0 0 1 44.3159 204.75)"
          fill={strokeStyle.fill}
          fontWeight="900"
          fontFamily={UnSelectableTextFontFamily}
          fontSize="7.5"
        >
          {t("Left")}
        </UnSelectableText>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 62 187)"
            fill={UnSelectableTextColor}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize={UnSelectableTextFont}
          >
            {renderPitchWithPolarity(leftPlusMinus, formValues.f1_lat)}
          </UnSelectableText>
          <rect
            x={39}
            y={168}
            width={46}
            height={30}
            style={rectStyle("f1_lat")}
            onClick={() => {
              clickableRectOnClickHandler("f1_lat");
            }}
          />
        </g>
        {/* Left Thumb Title */}
        {!is2Hander && (
          <UnSelectableText
            transform="matrix(1 0 0 1 102 402.883)"
            fill={strokeStyle.fill}
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {t("Left")}
          </UnSelectableText>
        )}
        <g>
          {/* LEFT Thumb Value */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 120 384)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize={UnSelectableTextFont}
            >
              {renderPitchWithPolarity(leftPlusMinus, formValues.thm_lat_l)}
            </UnSelectableText>
          )}
          {/* LEFT Thumb Clickable rect transparent */}
          {!is2Hander && (
            <rect
              x={96}
              y={365}
              width={46}
              height={30}
              style={rectStyle("thm_lat_l")}
              onClick={() => {
                clickableRectOnClickHandler("thm_lat_l");
              }}
            />
          )}
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 316 204.75)"
            fill={strokeStyle.fill}
            textAnchor="end"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {t("Right")}
          </UnSelectableText>
          <g>
            <UnSelectableText
              transform="matrix(1 0 0 1 300 187)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize={UnSelectableTextFont}
            >
              {renderPitchWithPolarity(rightPlusMinus, formValues.f2_lat)}
            </UnSelectableText>
            <rect
              x={276}
              y={168}
              width={46}
              height={30}
              style={rectStyle("f2_lat")}
              onClick={() => {
                clickableRectOnClickHandler("f2_lat");
              }}
            />
          </g>
        </g>
        <g>
          {/* Right Title Thumb */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 259 402.883)"
              fill={strokeStyle.fill}
              textAnchor="end"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="7.5"
            >
              {t("Right")}
            </UnSelectableText>
          )}
          <g>
            {/* RIGHT Thum Value */}
            {!is2Hander && (
              <UnSelectableText
                transform="matrix(1 0 0 1 243 384)"
                fill={UnSelectableTextColor}
                textAnchor="middle"
                fontWeight="900"
                fontFamily={UnSelectableTextFontFamily}
                fontSize={UnSelectableTextFont}
              >
                {renderPitchWithPolarity(rightPlusMinus, formValues.thm_lat_r)}
              </UnSelectableText>
            )}
            {/* RIGHT Thumb Clickable rect transparent */}
            {!is2Hander && (
              <rect
                x={220}
                y={366}
                width={46}
                height={30}
                style={rectStyle("thm_lat_r")}
                onClick={() => {
                  clickableRectOnClickHandler("thm_lat_r");
                }}
              />
            )}
          </g>
        </g>
        <g>
          {/* Forward Thumb Title */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 180 349.75)"
              fill={strokeStyle.fill}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="7.5"
            >
              {t("Forward")}
            </UnSelectableText>
          )}
          <g>
            {!is2Hander && (
              <UnSelectableText
                transform="matrix(1 0 0 1 181 332)"
                fill={UnSelectableTextColor}
                textAnchor="middle"
                fontWeight="900"
                fontFamily={UnSelectableTextFontFamily}
                fontSize={UnSelectableTextFont}
              >
                {formValues.thm_for &&
                formValues.thm_for !== "" &&
                formValues.thm_for !== "0.000" &&
                formValues.thm_for !== "0"
                  ? forwardPlusMinus
                  : ""}
                {formValues.thm_for}
              </UnSelectableText>
            )}
            {/* FORWARD Thumb Clickable rect transparent (first part of 2)*/}
            {!is2Hander && (
              <rect
                x={158}
                y={314}
                width={46}
                height={30}
                style={rectStyle("thm_for")}
                onClick={() => {
                  clickableRectOnClickHandler("thm_for");
                }}
              />
            )}
            {/* FORWARD Thumb Clickable rect transparent (second part of 2) */}
            {!is2Hander && (
              <rect
                x={158}
                y={314}
                width={46}
                height={30}
                style={rectStyle("thm_for")}
                onClick={() => {
                  clickableRectOnClickHandler("thm_for");
                }}
              />
            )}
          </g>
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 316 242.4829)"
            fill={strokeStyle.fill}
            textAnchor="end"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {t("Forward")}
          </UnSelectableText>
          <g>
            <UnSelectableText
              transform="matrix(1 0 0 1 300 225)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize={UnSelectableTextFont}
            >
              {formValues.f2_for &&
              formValues.f2_for !== "" &&
              formValues.f2_for !== "0.000" &&
              formValues.f2_for !== "0"
                ? forwardPlusMinusFingers
                : ""}
              {formValues.f2_for}
            </UnSelectableText>
            <rect
              x={276}
              y={207}
              width={46}
              height={30}
              style={rectStyle("f2_for")}
              onClick={() => {
                clickableRectOnClickHandler("f2_for");
              }}
            />
          </g>
        </g>
        <UnSelectableText
          transform="matrix(1 0 0 1 44.3164 129.1631)"
          fill={strokeStyle.fill}
          fontWeight="900"
          fontFamily={UnSelectableTextFontFamily}
          fontSize="7.5"
        >
          {t("Reverse")}
        </UnSelectableText>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 63 149)"
            fill={UnSelectableTextColor}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize={UnSelectableTextFont}
          >
            {formValues.f1_rev &&
            formValues.f1_rev !== "" &&
            formValues.f1_rev !== "0.000" &&
            formValues.f1_rev !== "0"
              ? backPlusMinusFingers
              : ""}
            {formValues.f1_rev}
          </UnSelectableText>
          <rect
            x={39}
            y={130}
            width={46}
            height={30}
            style={rectStyle("f1_rev")}
            onClick={() => {
              clickableRectOnClickHandler("f1_rev");
            }}
          />
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 316 129.1631)"
            fill={strokeStyle.fill}
            textAnchor="end"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {t("Reverse")}
          </UnSelectableText>
          <g>
            <UnSelectableText
              transform="matrix(1 0 0 1 300 150)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize={UnSelectableTextFont}
            >
              {formValues.f2_rev &&
              formValues.f2_rev !== "" &&
              formValues.f2_rev !== "0.000" &&
              formValues.f2_rev !== "0"
                ? backPlusMinusFingers
                : ""}
              {formValues.f2_rev}
            </UnSelectableText>
            <rect
              x={276}
              y={130}
              width={46}
              height={30}
              style={rectStyle("f2_rev")}
              onClick={() => {
                clickableRectOnClickHandler("f2_rev");
              }}
            />
          </g>
        </g>
        <g>
          {/* REVERSE Title Thum */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 181 418.2)"
              fill={strokeStyle.fill}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="7.5"
            >
              {t("Reverse")}
            </UnSelectableText>
          )}
          <g>
            {/* REVERSE Value */}
            {!is2Hander && (
              <UnSelectableText
                transform="matrix(1 0 0 1 182 437)"
                fill={UnSelectableTextColor}
                textAnchor="middle"
                fontWeight="900"
                fontFamily={UnSelectableTextFontFamily}
                fontSize={UnSelectableTextFont}
              >
                {formValues.thm_rev &&
                formValues.thm_rev !== "" &&
                formValues.thm_rev !== "0.000" &&
                formValues.thm_rev !== "0"
                  ? backPlusMinus
                  : ""}
                {formValues.thm_rev}
              </UnSelectableText>
            )}
            {/* REVERSE Thumb Clickable rect transparent */}
            {!is2Hander && (
              <rect
                x={158}
                y={418}
                width={46}
                height={30}
                style={rectStyle("thm_rev")}
                onClick={() => {
                  clickableRectOnClickHandler("thm_rev");
                }}
              />
            )}
          </g>
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 240 150.6001)"
            fill={strokeStyle.fill}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {t("Inserts")}
          </UnSelectableText>
          <g>
            <UnSelectableText
              transform="matrix(1 0 0 1 240 140)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize={formValues.f2_grip_type?.length > 16 ? "5.5" : "7"}
            >
              {formValues.f2_grip_type}
            </UnSelectableText>
            <rect
              x={206}
              y={125}
              width={68}
              height={25}
              style={rectStyle("f2_grip_type")}
              onClick={() => {
                clickableRectOnClickHandler("f2_grip_type");
              }}
            />
          </g>
        </g>
        {/* THUMB INSERT TYPE Title */}
        {!is2Hander && (
          <UnSelectableText
            transform="matrix(1 0 0 1 269 331.143)"
            fill={strokeStyle.fill}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="8.52"
          >
            {t("Thumb Insert Type")}
          </UnSelectableText>
        )}
        <g>
          {/* THUMB INSERT TYPE Value */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 268 342)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="8.52"
            >
              {formValues.thm_insert_type}
            </UnSelectableText>
          )}
          {/* THUMB INSERT TYPE Clickable rect transparent */}
          {!is2Hander && (
            <rect
              x={229}
              y={335}
              width={80}
              height={18}
              style={rectStyle("thm_insert_type")}
              onClick={() => {
                clickableRectOnClickHandler("thm_insert_type");
              }}
            />
          )}
        </g>
        {/* FLEXIBILITY Title */}
        {!is2Hander && (
          <UnSelectableText
            transform="matrix(1 0 0 1 111 331.143)"
            fill={strokeStyle.fill}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="8.52"
          >
            {t("Flexibility")}
          </UnSelectableText>
        )}
        <g>
          {/* FLEXIBILITY THUMB Value */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 110 342)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="8.52"
            >
              {formValues.thm_flex}
            </UnSelectableText>
          )}
          {/* FLEXIBILITY THUMB Clickable rect transparent */}
          {!is2Hander && (
            <rect
              x={89}
              y={335}
              width={44}
              height={18}
              style={rectStyle("thm_flex")}
              onClick={() => {
                clickableRectOnClickHandler("thm_flex");
              }}
            />
          )}
        </g>
        {/* OFFSET Title */}
        {!is2Hander && (
          <UnSelectableText
            transform="matrix(1 0 0 1 111 431.143)"
            fill={strokeStyle.fill}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="8.52"
          >
            {t("Offset")}
          </UnSelectableText>
        )}
        <g>
          {/* OFFSET Value */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 110 442)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="8.52"
            >
              {formValues.thm_offset}
            </UnSelectableText>
          )}
          {/* OFFSET Clickable rect transparent */}
          {!is2Hander && (
            <rect
              x={89}
              y={435}
              width={44}
              height={18}
              style={rectStyle("thm_offset")}
              onClick={() => {
                clickableRectOnClickHandler("thm_offset");
              }}
            />
          )}
        </g>
        <g>
          {/* OD Thumb Title */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 179.8242 380.442)"
              fill={strokeStyle.fill}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="7.5"
            >
              {t("O.D.")}
            </UnSelectableText>
          )}
          <g>
            {/* OD Thum Value */}
            {!is2Hander && (
              <UnSelectableText
                transform="matrix(1 0 0 1 180 371)"
                fill={UnSelectableTextColor}
                textAnchor="middle"
                fontWeight="900"
                fontFamily={UnSelectableTextFontFamily}
                fontSize={UnSelectableTextFont}
              >
                {formValues.thm_od}
              </UnSelectableText>
            )}
            {/* OD Thumb Clickable rect transparent */}
            {!is2Hander && (
              <rect
                x={150}
                y={350}
                width={62}
                height={32}
                style={rectStyle("thm_od")}
                onClick={() => {
                  clickableRectOnClickHandler("thm_od");
                }}
              />
            )}
          </g>
        </g>
        <g>
          {/*HOLE SIZE Thumb Title  */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 180 387.692)"
              fill={strokeStyle.fill}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="7.5"
            >
              {t("Hole Size")}
            </UnSelectableText>
          )}
          <g>
            {/* HOLE SIZE Thumb Value */}
            {!is2Hander && (
              <UnSelectableText
                transform="matrix(1 0 0 1 180 400)"
                fill={UnSelectableTextColor}
                textAnchor="middle"
                fontWeight="900"
                fontFamily={UnSelectableTextFontFamily}
                fontSize={UnSelectableTextFont}
              >
                {isOval
                  ? formValues.thm_oval_starting_bit
                  : formValues.thm_size}
              </UnSelectableText>
            )}
            {/* HOLE SIZE Thumb Clickable rect transparent */}
            {!is2Hander && (
              <rect
                x={150}
                y={380}
                width={62}
                height={32}
                style={rectStyle("thm_oval_starting_bit")}
                onClick={() => {
                  isOval
                    ? clickableRectOnClickHandler("thm_oval_starting_bit")
                    : clickableRectOnClickHandler("thm_size");
                }}
              />
            )}
          </g>
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 121.3086 182.3662)"
            fill={strokeStyle.fill}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {f1_insertless ? t("Oval") : t("O.D.")}
          </UnSelectableText>
          <g>
            <UnSelectableText
              transform="matrix(1 0 0 1 122 174)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize={UnSelectableTextFont}
            >
              {!f1_insertless && formValues.f1_od
                ? formValues.f1_od
                : formValues.f1_oval}
            </UnSelectableText>
            <rect
              x={92}
              y={152}
              width={62}
              height={32}
              style={rectStyle("f1_od")}
              onClick={() => {
                clickableRectOnClickHandler("f1_od");
              }}
            />
          </g>
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 122 189.6162)"
            fill={strokeStyle.fill}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {f1_insertless ? t("Hole Size") : t("Insert Size")}
          </UnSelectableText>
          <g>
            <UnSelectableText
              transform="matrix(1 0 0 1 122 202)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize={UnSelectableTextFont}
            >
              {formValues.f1_size}
            </UnSelectableText>
            <rect
              x={92}
              y={184}
              width={62}
              height={32}
              style={rectStyle("f1_size")}
              onClick={() => {
                clickableRectOnClickHandler("f1_size");
              }}
            />
          </g>
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 239.5645 182.3662)"
            fill={strokeStyle.fill}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {f2_insertless ? t("Oval") : t("O.D.")}
          </UnSelectableText>
          <g>
            <UnSelectableText
              transform="matrix(1 0 0 1 239 174)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize={UnSelectableTextFont}
            >
              {!f2_insertless && formValues.f2_od
                ? formValues.f2_od
                : formValues.f2_oval}
            </UnSelectableText>
            <rect
              x={208}
              y={152}
              width={62}
              height={32}
              style={rectStyle("f2_od")}
              onClick={() => {
                clickableRectOnClickHandler("f2_od");
              }}
            />
          </g>
        </g>
        <g>
          <UnSelectableText
            transform="matrix(1 0 0 1 240 189.6162)"
            fill={strokeStyle.fill}
            textAnchor="middle"
            fontWeight="900"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="7.5"
          >
            {f2_insertless ? t("Hole Size") : t("Insert Size")}
          </UnSelectableText>
          <g>
            <UnSelectableText
              transform="matrix(1 0 0 1 239 202)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize={UnSelectableTextFont}
            >
              {formValues.f2_size}
            </UnSelectableText>
            <rect
              x={208}
              y={182}
              width={62}
              height={32}
              style={rectStyle("f2_size")}
              onClick={() => {
                clickableRectOnClickHandler("f2_size");
              }}
            />
          </g>
        </g>
      </g>
      <g>
        {/*
          F / C / O / H. Only the spans this grip can produce are drawn, and
          they close ranks around the same centre so a hidden one leaves no
          hole in the row.
        */}
        {!is2Hander &&
          SPAN_BUTTONS.filter(({ span }) => spanVisibility[span]).map(
            ({ span, letter }, idx, shown) => {
              const cx =
                SPAN_ROW_CENTER_X -
                ((shown.length - 1) * SPAN_ROW_GAP) / 2 +
                idx * SPAN_ROW_GAP;

              return (
                <g key={span}>
                  <circle
                    fill={getSpanButtonFillColor(shownSpan, span, formValues)}
                    stroke={strokeStyle.stroke}
                    strokeWidth={strokeStyle.strokeWidthPath}
                    cx={cx}
                    cy="227"
                    r="8"
                    onClick={() =>
                      specsheetSelectSpanType({ selectedSpan: span })
                    }
                  />
                  <UnSelectableText
                    transform={`matrix(1 0 0 1 ${cx} 230)`}
                    fill={getSpanButtonTextFillColor(
                      shownSpan,
                      span,
                      formValues
                    )}
                    textAnchor="middle"
                    fontWeight="900"
                    fontFamily={UnSelectableTextFontFamily}
                    fontSize="8"
                    onClick={() =>
                      specsheetSelectSpanType({ selectedSpan: span })
                    }
                  >
                    {letter}
                  </UnSelectableText>
                </g>
              );
            }
          )}
        {/*  Text Circle Button Selected */}
        {!is2Hander && (
          <UnSelectableText
            transform="matrix(1 0 0 1 181 244)"
            fill={strokeStyle.fill}
            textAnchor="middle"
            fontFamily={UnSelectableTextFontFamily}
            fontSize="8"
            fontWeight="bold"
          >
            {getSelectedSpanTitle(shownSpan, t)}
          </UnSelectableText>
        )}
        <g>
          {/* f1SPAN Value */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 131 277)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="18"
            >
              {formValues[`f1_span_${shownSpan}`]}
            </UnSelectableText>
          )}
          {/* f1SPAN Clickable rect transparent */}
          {!is2Hander && (
            <rect
              x={92}
              y={248}
              width={80}
              height={46}
              style={clickableRectStyle}
              onClick={() => {
                clickableRectOnClickHandler(`f1_span_${shownSpan}`);
              }}
            />
          )}
        </g>
        <g>
          {/* f2SPAN Value */}
          {!is2Hander && (
            <UnSelectableText
              transform="matrix(1 0 0 1 232 277)"
              fill={UnSelectableTextColor}
              textAnchor="middle"
              fontWeight="900"
              fontFamily={UnSelectableTextFontFamily}
              fontSize="18"
            >
              {formValues[`f2_span_${shownSpan}`]}
            </UnSelectableText>
          )}
          {/* f2SPAN Clickable rect transparent */}
          {!is2Hander && (
            <rect
              x={190}
              y={248}
              width={80}
              height={46}
              style={clickableRectStyle}
              onClick={() => {
                clickableRectOnClickHandler(`f2_span_${shownSpan}`);
              }}
            />
          )}
        </g>
      </g>
    </svg>
  ) : (
    <></>
  );
};

/**
 * The fields the drawing itself can be tapped on.
 *
 * Exported so the scan review can tell which values already have somewhere to
 * be edited and only list the ones that do not.
 *
 * The two span boxes are not one field each: they read whichever of the
 * F/C/O/H buttons is selected, so each reaches four. Deriving this list from
 * the literal handler calls alone missed all eight and put the spans in the
 * rows below, where they had a picker one tap away all along.
 */
export const TAPPABLE_FIELDS = [
  "bridge",
  "clt",
  "f1_for",
  "f1_grip_type",
  "f1_lat",
  "f1_od",
  "f1_rev",
  "f1_size",
  "f1_span_cut",
  "f1_span_full",
  "f1_span_hole_to_hole",
  "f1_span_outer2_cut",
  "f2_for",
  "f2_grip_type",
  "f2_lat",
  "f2_od",
  "f2_rev",
  "f2_size",
  "f2_span_cut",
  "f2_span_full",
  "f2_span_hole_to_hole",
  "f2_span_outer2_cut",
  "thm_flex",
  "thm_for",
  "thm_insert_type",
  "thm_lat_l",
  "thm_lat_r",
  "thm_od",
  "thm_offset",
  "thm_oval_starting_bit",
  "thm_rev",
  "thm_size",
];


export const SpecsheetSVG = connect(
  (state, ownProps) => {
    const appSettings = state.settings.appSettings;
    const rightPlusMinus = appSettings.drillPressCalibration
      ?.oval_machine_show_pitch_plus_minus
      ? appSettings.drillPressCalibration?.oval_machine_right_positive
        ? "+"
        : "-"
      : "";
    const leftPlusMinus = appSettings.drillPressCalibration
      ?.oval_machine_show_pitch_plus_minus
      ? appSettings.drillPressCalibration?.oval_machine_right_positive
        ? "-"
        : "+"
      : "";

    const forwardPlusMinus = appSettings.drillPressCalibration
      ?.oval_machine_show_pitch_plus_minus
      ? appSettings.drillPressCalibration?.oval_machine_forward_thumb_positive
        ? "+"
        : "-"
      : "";
    const backPlusMinus = appSettings.drillPressCalibration
      ?.oval_machine_show_pitch_plus_minus
      ? appSettings.drillPressCalibration?.oval_machine_forward_thumb_positive
        ? "-"
        : "+"
      : "";

    const forwardPlusMinusFingers = appSettings.drillPressCalibration
      ?.oval_machine_show_pitch_plus_minus
      ? appSettings.drillPressCalibration?.oval_machine_forward_thumb_positive
        ? "-"
        : "+"
      : "";
    const backPlusMinusFingers = appSettings.drillPressCalibration
      ?.oval_machine_show_pitch_plus_minus
      ? appSettings.drillPressCalibration?.oval_machine_forward_thumb_positive
        ? "+"
        : "-"
      : "";

    // connect() lets state win over ownProps, so an explicit formValues has to
    // be honoured here rather than simply passed in. The scan review renders
    // this same sheet against values that are not in the store yet.
    const previewValues = ownProps.formValues;

    return {
      appSettings,
      selectedSpan: state.specsheet.selectedSpan,
      formValues: previewValues || (state.specsheet.currentSpecsheetAuditRecord
        ? specsheetParser(state.settings.appSettings)(
            state.specsheet.currentSpecsheetAuditRecord
          )
        : specsheetParser(state.settings.appSettings)(
            state.specsheet.currentSpecSheet
          )),
      isDesktop: true,
      isOval: R.path(["thm_type"])(state.specsheet.currentSpecSheet) === "Oval",
      isOvalPreviewOpen: state.specsheet.isOvalPreviewOpen,
      isRightHander: previewValues
        ? previewValues.hand !== "left_hander"
        : state.specsheet.currentSpecSheet
          ? state.specsheet.currentSpecSheet.hand === "right_hander"
          : true,
      rightPlusMinus,
      leftPlusMinus,
      forwardPlusMinus,
      backPlusMinus,
      forwardPlusMinusFingers,
      backPlusMinusFingers,
      isReadOnly:
        ownProps.isReadOnly !== undefined
          ? ownProps.isReadOnly
          : state.specsheet.currentSpecsheetAuditRecord
            ? true
            : state.userSession.isReadOnly,
      flaggedFields: ownProps.flaggedFields,
      onFieldClick: ownProps.onFieldClick,
      onFieldValueChange: ownProps.onFieldValueChange,
      activeField: ownProps.activeField,
      is2Hander:
        state.bowlers.selectedCustomer?.style === "two_handed" ||
        state.bowlers.selectedCustomer?.style === "thumbless",
    };
  },
  { specsheetSelectSpanType, specsheetSVGClick, specsheetHandleForm }
)(SpecsheetSVGComponent);
